/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { InjectedIntlProps, injectIntl } from "react-intl";
import {
  Stack,
  Label,
} from "@fluentui/react";
import {
  deleteConsumptionAPI,
  getConsumptionAPI,
  putConsumptionAPI,
} from "../../../../utils/httpUtils";
import { useMyContext } from "../../../../context/myContext";
import { useTileContext } from "../../../../context/TileContext";
import ShimmerLoader from ".././ShimmerLoader";
import {
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
  setIsBotError,
} from "../../../../../core/store";
import { useDispatch } from "react-redux";
import {
  WEBFORM_IRIS_UTTERANCE,
  PURL_ERROR_TILE_LOCKED,
  NON_AI_Telematry_message,
  ai_servie_detail_panel_cancel,
  non_ai_service_detail_panel_cancel,
  ai_service_detail_cancel_message,
  non_ai_service_detail_cancel_message,
  AI_EMPTY_LABEL,
  Clicked_On_Details,
  Clicked_On_Start,
  Home_Page_Start_Telematry_message,
  Recommended_Tile_Start_Telematry_message,
  Recommended_Tile_Details_Telematry_message,
} from "../../../../utils/constants";
import { getFormNames } from "../../../TileConfiguration/V2_APIHelperFile";
import ErrorComponent from "../../../CatalogPage/ErrorComponent";
import { lazyRetry } from "../../../../utils/utils";
import { makeStyles, tokens, Text } from "@fluentui/react-components";
import { getTileProfileIfExists, TileProfileResponse } from "../../../../utils/segmentApiUtils";
import { useWorkspaceTiles, useSegmentSelection, usePUrlNavigation } from "./hooks";
import { CardElements } from "./components/TileCard";
import { SegmentSelector } from "./components/SegmentSelector";
import { useServiceRouter } from "./components/ServiceRouter";
import { trackTileAgentHandoff } from "../../../copilot/utils/tileAgentHandoffTelemetry";
import { loadCopilotAccessMap, CopilotAccessMap, resolveTileHandoffKey } from "../../../../utils/copilotAccessMap";
import { resolvePdmSupportAssuranceTitle } from "../../../WebFormPanel/WebFormUtils/pdmSupportAssuranceFeature";

// Agent routing constants

// Error messages

export interface TileComponentProp extends InjectedIntlProps {
  // 5 SHARED props — still passed from V2_Main (used by sibling components)
  setActiveTabKey?: any;
  userId: any;
  isUserLoggedIn: any;
  setRefreshHeaderTabCount: any;
  refreshHeaderTabCount: boolean;
  // 24 props removed — now sourced exclusively from TileContext
}

const InfoPanel = lazy(() => lazyRetry(() => import("./V2_InfoPanel")));
const PopUpModal = lazy(() => lazyRetry(() => import(".././PopUpPanel")));
/**
 * Represents a tile component.
 *
 * @component
 * @param {TileComponentProp} props - The component props.
 * @returns {React.ReactElement} The rendered tile component.
 */

// Case-insensitive URLSearchParams helpers (URL param keys like Tilename/TileName/tilename all match)
const urlParamHas = (params: URLSearchParams, name: string): boolean => {
  const lower = name.toLowerCase();
  for (const key of Array.from(params.keys())) {
    if (key.toLowerCase() === lower) return true;
  }
  return false;
};
const urlParamGet = (params: URLSearchParams, name: string): string | null => {
  const lower = name.toLowerCase();
  for (const [key, value] of Array.from(params.entries())) {
    if (key.toLowerCase() === lower) return value;
  }
  return null;
};

// Module-level Set for O(1) PURL key validation (avoids per-call array allocation)
const KNOWN_PURL_KEYS = new Set(['tilename', 'irisutterance', 'area', 'mode', 'segment', 'subsegment', 'tileinfo', 'sendservicegroup', 'defaultservicegroup', 'downloadfile', 'typeoffeedback', 'prompt', 'appname']);

const TileComponent: React.FC<TileComponentProp> = (props) => {
  const { data, updateContextData } = useMyContext();
  const ctx = useTileContext();
  const reduxDispatch = useDispatch();
  const userId = ctx?.userId ?? props.userId;
  // --- Context-first bindings (PR 3.4: context is sole source for 24 props) ---
  const isUserLoggedIn = ctx?.isUserLoggedIn ?? props.isUserLoggedIn;
  const addServiceId = ctx?.addServiceId;
  const setAddServiceId = ctx?.setAddServiceId;
  const userDataChanged = ctx?.userDataChanged;
  const currentUserData = ctx?.currentUserData;
  const reccomendedData = ctx?.reccomendedData;
  const recommendationLoaded = ctx?.recommendationLoaded;
  const setRecommendationLoaded = ctx?.setRecommendationLoaded;
  const userWelcomeUpdated = ctx?.userWelcomeUpdated;
  const finalTileObject = ctx?.finalTileObject;
  const setProfileLoading = ctx?.setProfileLoading;
  const setShowBot = ctx?.setShowBot;
  const setShowErrorModal = ctx?.setShowErrorModal;
  const isCanceledPreset = ctx?.isCanceledPreset;
  const showErrorModal = ctx?.showErrorModal;
  const setActiveTabKey = ctx?.setActiveTabKey ?? props.setActiveTabKey;
  const enableWwicCopilot = ctx?.enableWwicCopilot;
  const setIrisBotIconClicked = ctx?.setIrisBotIconClicked;
  const setIrisUtterance_shell = ctx?.setIrisUtterance;
  const setChatBotClicked = ctx?.setChatBotClicked;
  const setRefreshHeaderTabCount = ctx?.setRefreshHeaderTabCount ?? props.setRefreshHeaderTabCount;
  const refreshHeaderTabCount = ctx?.refreshHeaderTabCount ?? props.refreshHeaderTabCount;
  const setServiceDeleted_shell = ctx?.setServiceDeleted;
  const setTileName_shell = ctx?.setTileName;
  const setIrisAppName = ctx?.setIrisAppName;
  const setSegment = ctx?.setSegment;
  const setSubSegment = ctx?.setSubSegment;
  const setFormDataToIris = ctx?.setFormDataToIris;
  const [openPanelId, setOpenPanelId] = React.useState(null);
  const autoPinAttempted = useRef(false);
  const autoPinInProgress = useRef(false);
  const copilotMapFetched = useRef(false);
  const lastPurlTileName = useRef<string | null>(null);
  const purlProcessed = useRef(false);
  const callbacksRef = useRef<Record<string, any>>({});

  const [isPUrlSegmentTile, setIsPUrlSegmentTile] = useState(false);
  const [urlTileData, setUrlTileData] = useState(null);
  const [copilotAccessMap, setCopilotAccessMapState] = useState<CopilotAccessMap>({});
  const copilotAccessMapLoaded = useRef(false);
  const setCopilotAccessMap = React.useCallback((map: CopilotAccessMap) => {
    copilotAccessMapLoaded.current = true;
    setCopilotAccessMapState(map);
  }, []);
  const copilotAccessRequest = useRef(0);
  const [isDetailClick, setDetailClick] = useState(false);
  const [isStartClick, setStartClick] = useState(false);
  const [tileSelected, setTileSelected] = useState(null);
  const [isTileHasForm, setIsTileHasForm] = useState(false);
  const [isEneterprise, setEnterprise] = useState(false);
  const [isopenSegment, SetIsopenSegment] = useState(true);
  const [userSelectedOption, setUserSelectedOption] = useState("");
  const [isRedirectFromAnotherForm, setIsRedirectFromAnotherForm] = useState(false);
  // WI-1249082: Tile-scoped flag set when copilot "Create Incident" button is clicked.
  // Stores the serviceID of the tile that requested form opening (null = no request).
  // Prevents bleed between tiles if user switches without dismiss.
  const [formOpenRequestedForTileId, setFormOpenRequestedForTileId] = useState<number | null>(null);
  const [tileProfileResponse, setTileProfileResponse] = useState<TileProfileResponse>({ isSuccess: false, profileJson: null });
  const [allowRendering, setAllowRendering] = useState<boolean>(true);

  // --- Extracted hooks (Phase 1) ---
  const {
    dataServiceWorkspace, setDataServiceWorkspace,
    dialogTitle, setDialogTitle,
    isDeleteModalVisible, setIsDeleteModalVisible,
    isDropdownModalVisible, setIsDropdownModalVisible,
    showTileNotFoundModal, setShowTileNotFoundModal,
    isErrorModalVisible, setIsErrorModalVisible,
    deleteModalId, setDeleteModalId,
    deleteId, setDeleteId,
    selectedOptions, setSelectedOptions,
    irisAppName,
    context,
    value, setValue,
    isLoading, setIsLoading,
    isLoadingRecommended, setIsLoadingRecommended,
    resultError, setResultError,
    errorData, setErrorData,
    webFormIrisContext, setWebFormIrisContext,
    recommendedTile, setRecommendedTile,
    ResultErrorRecommendation, setResultErrorRecommendation,
    isWebFormPanelOpen, setIsWebFormPanelOpen,
    selectedTileId, setSelectedTileId,
    selectedServiceTile, setSelectedServiceTile,
    isTileFoundInPreference, setIsTileFoundInPreference,
    tileAlreadyInWorkspace, setTileAlreadyInWorkspace,
    serviceDeleted, setServiceDeleted,
    webFormNames, setWebFormNames,
    isGetTileExecuted, setGetTileExecuted,
    isWorkspaceLoad, setWorkspaceLoad,
    WEBFORMNAMES_LOCAL_STORAGE_KEY,
    isUserDataChanged,
    reduxDispatch: _wsReduxDispatch,
    fetchUserWorkspacesAndDefaultTiles,
    validateResponse,
    processRedirectServices,
    getTiles,
    getUnsecuredRecommendedTiles,
    getRecommendedTiles,
    fetchTilesData,
    checkUrlParams,
    handleNonAmericaOrAmerica,
    handleLoadedOrNonAmerica,
    handleUserDataChange,
    fetchData,
    addToWorkspace,
    fetchTileVisibility,
    logUserPinClick,
    isTileVisibleForUser,
    getStoredResult,
    handleTileFoundInCatalog,
    handleTileNotFoundInCatalog,
  } = useWorkspaceTiles({
    userId,
    addServiceId,
    setAddServiceId,
    userDataChanged,
    currentUserData,
    reccomendedData,
    recommendationLoaded,
    setRecommendationLoaded,
    userWelcomeUpdated,
    finalTileObject,
    setProfileLoading,
    setShowBot,
    setShowErrorModal,
    isCanceledPreset,
    autoPinInProgress,
    checkTileSecurity: (...args: any[]) => callbacksRef.current.checkTileSecurity?.(...args),
    purlProcessed,
    isUserLoggedIn,
    setActiveTabKey,
    copilotMapFetched,
    setCopilotAccessMap,
  });

  const {
    segmentOptions, setSegmentOptions,
    subSegmentOptions, setSubSegmentOptions,
    segmentFinal, setSegmentFinal,
    subSegmentFinal, setSubSegmentFinal,
    segmentDropdownVisible, setSegmentDropdownVisible,
    pendingSegmentState, setPendingState,
    irisUtterance, setIrisUtterance,
    appName, setAppName,
    tileName, setTileName,
    getAllSegments,
    getAllSubSegmentFromSegment,
    passSegmentDataToIris,
    openPanelForSMEC,
    fetchDetails,
    hideSegmentDropdown,
    hideSegmentDropdownAfterSave,
    resetRedirectState,
    handleFormAndSegment,
    openWebFormFinal,
  } = useSegmentSelection({
    setShowBot,
    enableWwicCopilot,
    setIrisBotIconClicked,
    finalTileObject,
    dataServiceWorkspace,
    errorData,
    setErrorData,
    isPUrlSegmentTile,
    setIsPUrlSegmentTile,
    urlTileData,
    setUrlTileData,
    purlProcessed,
    checksIris: (...args: any[]) => callbacksRef.current.checksIris?.(...args),
    handleChatBotIconClick: (...args: any[]) => callbacksRef.current.handleChatBotIconClick?.(...args),
    openWebForm: (...args: any[]) => callbacksRef.current.openWebForm?.(...args),
    setInfoPanelZIndex: (...args: any[]) => callbacksRef.current.setInfoPanelZIndex?.(...args),
    fetchUserWorkspacesAndDefaultTiles,
    validateResponse,
    setDataServiceWorkspace,
    handlePanelOpening: (...args: any[]) => callbacksRef.current.handlePanelOpening?.(...args),
    isRedirectFromAnotherForm,
    setIsRedirectFromAnotherForm,
    isopenSegment,
    SetIsopenSegment,
    userSelectedOption,
    setUserSelectedOption,
    setTileProfileResponse,
    setIsDropdownModalVisible,
    irisAppName,
    selectedTileId,
    setSelectedTileId,
    tileSelected,
    setTileSelected,
    setOpenPanelId,
    setDialogTitle,
    checkIrisService: (...args: any[]) => callbacksRef.current.checkIrisService?.(...args),
    checkTileFormStatus: (...args: any[]) => callbacksRef.current.checkTileFormStatus?.(...args),
    webFormNames,
    isTileHasForm,
    setIsTileHasForm,
    reduxDispatch,
    copilotAccessMap,
  });

  const {
    validateURL,
    startTileFlowBasedOnSrc,
    handleTileNotLoaded,
    showPurlTileNotFoundAndRedirect,
  } = usePUrlNavigation({
    finalTileObject,
    setProfileLoading,
    setShowBot,
    setIrisUtterance: setIrisUtterance_shell,
    setChatBotClicked,
    setIrisBotIconClicked,
    setActiveTabKey,
    setRefreshHeaderTabCount,
    refreshHeaderTabCount,
    isIncidentTile: finalTileObject.isIncidentTile,
    dataServiceWorkspace,
    setDataServiceWorkspace,
    isTileFoundInPreference,
    setGetTileExecuted,
    setShowTileNotFoundModal,
    updateContextData,
    fetchUserWorkspacesAndDefaultTiles,
    validateResponse,
    processRedirectServices,
    setDialogTitle,
    setIrisUtteranceLocal: setIrisUtterance,
    setAppName,
    setTileName,
    setSelectedTileId,
    setTileSelected,
    setDetailClick,
    handleStartClick: () => callbacksRef.current.handleStartClick?.(),
    checkTileSecurity: (...args: any[]) => callbacksRef.current.checkTileSecurity?.(...args),
    addToWorkspace,
    openPanel: (...args: any[]) => callbacksRef.current.openPanel?.(...args),
    checkIrisService: (...args: any[]) => callbacksRef.current.checkIrisService?.(...args),
    setInfoPanelZIndex: (...args: any[]) => callbacksRef.current.setInfoPanelZIndex?.(...args),
    PresetPreference: () => callbacksRef.current.PresetPreference?.(),
    autoPinAttempted,
    autoPinInProgress,
    lastPurlTileName,
    purlProcessed,
    isPUrlSegmentTile,
    setIsPUrlSegmentTile,
    urlTileData,
    setUrlTileData,
    copilotAccessMap,
    setTileNameShell: setTileName_shell,
  });

  const styles = useStyles();

  // When detail panel is open, add body class and suppress IRIS overlay via DOM
  React.useEffect(() => {
    if (!isDetailClick) {
      document.body.classList.remove('detail-panel-open');
      return undefined;
    }

    document.body.classList.add('detail-panel-open');

    // Force IRIS layers behind the detail panel whenever they appear
    const suppressIris = () => {
      const irisLayers = document.querySelectorAll(
        '#fluent-default-layer-host > .ms-Layer'
      );
      irisLayers.forEach((layer) => {
        if (layer.querySelector('.irisBot') || layer.querySelector('#irisMenu')) {
          (layer as HTMLElement).style.zIndex = '1';
        }
      });
      const botContainer = document.querySelector('.bot-container') as HTMLElement;
      if (botContainer) botContainer.style.zIndex = '1';
    };

    suppressIris();
    // MutationObserver to catch IRIS loading after detail panel opens
    const observer = new MutationObserver(suppressIris);
    const layerHost = document.getElementById('fluent-default-layer-host');
    if (layerHost) {
      observer.observe(layerHost, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      document.body.classList.remove('detail-panel-open');
      // Restore IRIS z-index
      const irisLayers = document.querySelectorAll(
        '#fluent-default-layer-host > .ms-Layer'
      );
      irisLayers.forEach((layer) => {
        if (layer.querySelector('.irisBot') || layer.querySelector('#irisMenu')) {
          (layer as HTMLElement).style.zIndex = '';
        }
      });
      const botContainer = document.querySelector('.bot-container') as HTMLElement;
      if (botContainer) botContainer.style.zIndex = '';
    };
  }, [isDetailClick]);

  // WI-1249082: Listen for 'open-form-panel' from copilot "Create an Incident Request" button.
  // WI-1249082: Listen for 'open-form-panel' from copilot "Create an Incident Request" button.
  // Triggers the same form-opening flow as Start button — reuses handleStartButtonFormLogic
  // which already handles segment, Enterprise/Corp redirect, and form key resolution.
  React.useEffect(() => {
    const handleOpenFormPanel = async () => {
      if (selectedTileId) {
        const card = dataServiceWorkspace.find((srv) => srv.serviceID === selectedTileId);
        if (!card) return;

        // Reset first so React detects the change even if same tile (user clicks incident
        // button again after switching to Service Info tab). This triggers a false→true
        // transition on formOpenRequested, re-firing the useEffect in V2_InfoPanel.
        setFormOpenRequestedForTileId(null);
        await new Promise(resolve => setTimeout(resolve, 0));

        // Reuse the same logic as Start button — handles segment/Enterprise/redirect/form tab
        const tileProfileResult = await fetchTileProfile(selectedTileId);
        const redirected = await handleStartButtonFormLogic(selectedTileId, tileProfileResult);

        // Determine which tile actually has the form AFTER redirect logic runs:
        // - Enterprise stays on source tile (redirected=false)
        // - Corp/SME&C redirects to target tile (redirected=true, openPanelId already set)
        const actualFormTileId = redirected && card.isRedirect && card.redirectServiceIdForSMEC
          ? card.redirectServiceIdForSMEC
          : selectedTileId;
        setFormOpenRequestedForTileId(actualFormTileId);

        // Ensure the info panel is open (handleStartButtonFormLogic sets form state but not panel ID)
        if (!redirected) {
          setOpenPanelId(selectedTileId);
        }
      }
    };
    window.addEventListener('open-form-panel', handleOpenFormPanel);
    return () => window.removeEventListener('open-form-panel', handleOpenFormPanel);
  }, [selectedTileId, dataServiceWorkspace, copilotAccessMap]);

  // Helper: silently close MCAPS Copilot panel if open (no confirmation dialog).
  // Called before starting a new tile so the previous copilot panel doesn't overlay.
  const closeMCAPSPanel = () => {
    try {
      if (document.body.classList.contains('mcaps-panel-open')) {
        window.dispatchEvent(new CustomEvent('mcapsCopilot_requestClose'));
      }
    } catch { }
  };

  // Helper: set z-index on Fluent UI InfoPanel Layer portal element.
  // Uses retry instead of fixed delay since panel render time varies.
  const setInfoPanelZIndex = (zIndex: string, maxRetries = 5, attempt = 0): void => {
    const el = document.querySelector(".infoPanelHeader");
    if (el) {
      const layer = el.closest(".ms-Layer") as HTMLElement;
      if (layer) layer.style.zIndex = zIndex;
    } else if (attempt < maxRetries) {
      setTimeout(() => setInfoPanelZIndex(zIndex, maxRetries, attempt + 1), 200);
    }
  };

  const handleDetailClick = () => {
    setDetailClick(true);
    //on click of detal button make z-index high of infopannel
    setTimeout(() => {
      let contentInner = document.querySelector(
        ".infoPanelHeader"
      ) as HTMLElement;

      if (contentInner) {
        let grandGrandParent = contentInner.closest(
          ".ms-Layer.ms-Layer"
        ) as HTMLElement;

        if (grandGrandParent) {
          grandGrandParent.style.zIndex = "9999910";
        }
      }
    }, 500); // Delay of mili seconds
    //on click of detal button make z-index high of infopannel
    setTimeout(() => {
      let contentInner = document.querySelector(
        ".infoPanelHeader"
      ) as HTMLElement;

      if (contentInner) {
        let grandGrandParent = contentInner.closest(
          ".ms-Layer.ms-Layer"
        ) as HTMLElement;

        if (grandGrandParent) {
          grandGrandParent.style.zIndex = "9999910";
        }
      }
    }, 500); // Delay of mili seconds
  };

  const handleStartClick = () => {
    setStartClick(true);
  };


  const checkTileSecurity = async (tilename) => {
    const services = sessionStorage.getItem("v2_services");

    if (services) {
      try {
        const tiles = JSON.parse(services);
        const tile = tiles.find((tile) => tile.name === tilename);
        return tile ? tile.isSecuredByAzureADGroup : false;
      } catch (e) {
        console.error("Error parsing services data", e);
        return false;
      }
    }

    // Fallback if services are not found
    let securedTileNamesData = localStorage.getItem("securedTileNames");
    if (!securedTileNamesData) {
      return false;
    }

    try {
      const securedTileNames = JSON.parse(securedTileNamesData);
      return (
        Array.isArray(securedTileNames) && securedTileNames.includes(tilename)
      );
    } catch (e) {
      console.error("Error parsing securedTileNames data", e);
      return false;
    }
  };

  useEffect(() => {
    if (context) {
      fetchWebFormNames();
      cleanUpOldVersions(); // Clean up old versions when the app loads
    }
  }, [context]);

  const PresetPreference = async () => {
    const area = sessionStorage.getItem("AreaName");
    const role = sessionStorage.getItem("RoleName");
    const segment = sessionStorage.getItem("SegmentName");
    const subSegment = sessionStorage.getItem("SubSegmentName");

    try {
      const apiUrl = "/api/V2_User/PresetPreference";
      const postData = {
        //UPN: currentUser.email,
        //Oid: currentUser.userObject.localAccountId,
        UserArea: area,
        UserRole: role,
        Segment: segment,
        SubSegment: subSegment,
        DataverseRowID: "3f5a37dc-8d95-4232-85a7-990dab5888a9",
        IsActive: 1,
        IsWelcomeMessage: currentUserData.isWelcomeMessage,
      };

      const response = await putConsumptionAPI(
        apiUrl,
        postData,
        context.authClient
      );

      if (response.status >= 200 && response.status < 300) {
        finalTileObject.loadForms = true;
        setWorkspaceLoad(true);
      }
    } catch (error) {
      console.error("An error occurred while fetching the API.", error);
    }
  };


  // Common helper function for panel opening logic
  const handlePanelOpening = (id, workspaceData, isRecommended, isStartClick) => {
    const filteredService = !isRecommended
      ? workspaceData.filter((srv) => srv.serviceID === id)
      : recommendedTile.filter((srv) => srv.serviceID === id);

    // Event logging
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: filteredService[0].name,
        RequestType: "",
        TileName: filteredService[0].tileName,
        IsNonIrisService: filteredService[0].service_IsNonIRISService
          ? "true"
          : "false",
        IrisUtterance: filteredService[0].iriS_Utterance,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETLogEventData({
        elementName: isStartClick ? Clicked_On_Start : Clicked_On_Details,
        elementId: "",
        action: "click",
        message: isStartClick ? isRecommended ? Recommended_Tile_Start_Telematry_message : Home_Page_Start_Telematry_message : (isRecommended
          ? Recommended_Tile_Details_Telematry_message
          : NON_AI_Telematry_message),
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));

    return filteredService[0];
  };

  // Helper function to fetch tile profile
  const fetchTileProfile = async (id: number) => {
    try {
      const tileProfileResult = await getTileProfileIfExists(context, id);
      setTileProfileResponse(tileProfileResult);
      return tileProfileResult;
    } catch (error) {
      console.error(`Failed to fetch tile profile for ID ${id}:`, error);
      const errorResult = { isSuccess: false, profileJson: null };
      setTileProfileResponse(errorResult);
      return errorResult;
    }
  };

  // Helper function to check if tile has form and set form state
  const checkTileFormStatus = (card: any) => {
    // Click on the Start button of a tile then close the form then click on the start button of 'Account Management Requests (Americas Enterprise only)' tile then the form is not opening, instead detail page is opening, to resolve that below is the code.
    const isAmerica = card.iriS_Utterance === WEBFORM_IRIS_UTTERANCE;

    // Use webFormNames from state; if not yet loaded (async race), fallback to localStorage
    let formList = webFormNames;
    if (!formList || formList.length === 0) {
      try {
        const stored = localStorage.getItem(WEBFORMNAMES_LOCAL_STORAGE_KEY);
        if (stored) {
          formList = JSON.parse(stored);
        }
      } catch {
        formList = [];
      }
    }

    const service = formList?.find(
      (item) =>
        normalizeString(item.tileName) === normalizeString(card.tileName) &&
        Boolean(item.isAmerica) === isAmerica
    );

    const serviceWithFormKey = service?.formKey;
    if (serviceWithFormKey !== undefined) {
      setIsTileHasForm(true);
      return serviceWithFormKey;
    } else {
      setIsTileHasForm(false);
    }
    return undefined;
  };

  // Handle form opening logic for start button clicks
  const handleStartButtonFormLogic = async (id: number, tileProfileResult: TileProfileResponse): Promise<boolean> => {
    const card = dataServiceWorkspace.find((srv) => srv.serviceID === id);
    if (!card) return false; // Guard against missing card

    // If no saved profile and tile has segment, show segment dropdown (same as openPanel)
    if (!tileProfileResult.profileJson) {
      if (card.isSegment) {
        const tileKey = (card.tileName || '').toLowerCase();
        const isAmerica = card.iriS_Utterance === WEBFORM_IRIS_UTTERANCE;
        // Use webFormNames from state; fallback to localStorage if not loaded yet
        let formList = webFormNames;
        if (!formList || formList.length === 0) {
          try {
            const stored = localStorage.getItem(WEBFORMNAMES_LOCAL_STORAGE_KEY);
            if (stored) formList = JSON.parse(stored);
          } catch { formList = []; }
        }
        const service = formList?.find(
          (item) =>
            normalizeString(item.tileName) === normalizeString(card.tileName) &&
            Boolean(item.isAmerica) === isAmerica
        );
        const serviceWithFormKey = service?.formKey;
        // CopilotADGroupMapping.enableTileForm = 0 → webform must NOT open even if a formKey exists
        const enableTileFormFlag = copilotAccessMap[tileKey]?.enableTileForm;
        const isTileConsistsWebform = enableTileFormFlag !== false &&
          (serviceWithFormKey !== undefined || enableTileFormFlag === true);
        handleFormAndSegment(isTileConsistsWebform, card.name, card.iriS_Utterance, serviceWithFormKey, card.tileName);
        return true; // segment dialog shown — caller should NOT switch tabs yet
      }
      // Non-segment tile with no saved profile — open form directly (no segment selection needed)
      const serviceWithFormKey = checkTileFormStatus(card);
      if (serviceWithFormKey) {
        openWebForm(card.name, card.iriS_Utterance, card.tileName, serviceWithFormKey);
        sessionStorage.setItem("WebFormOpen", "true");
        setIsTileHasForm(true);
      } else {
        setIsTileHasForm(false);
      }
      return false;
    }

    // Handle enterprise / MS Elevate segment detection
    const segments = tileProfileResult.profileJson?.segments?.toString();
    let isEnterprise = false;
    if (segments) {
      const selectedSegment = segmentOptions.find(option => option.key.toString() === segments);
      const segmentText = selectedSegment?.text?.toLowerCase();
      // Enterprise and MS Elevate are the top-tier segments: both stay on THIS tile's own
      // form / IFrame and must NEVER redirect to the SME&C/Corp target form. Only SME&C/Corp
      // redirect. Treating them identically keeps "Enterprise" and "MS Elevate" on the same form
      // (e.g. "Account Management Requests (Americas Enterprise only)") and on the same IFrame
      // (e.g. AM Tool), instead of MS Elevate falling through to the redirect target form.
      if (segmentText === "enterprise" || segmentText === "ms elevate") {
        setEnterprise(true);
        isEnterprise = true;
      }
    }

    // For redirect tiles (e.g., AMR Americas → AMR) with saved SME&C/Corp profile,
    // open the target tile's form directly instead of the source tile's form.
    // Enterprise / MS Elevate users stay on the source form; only Corp/SME&C redirect.
    if (card.isRedirect && card.redirectServiceIdForSMEC && !isEnterprise) {
      const targetCard = dataServiceWorkspace.find((srv) => srv.serviceID === card.redirectServiceIdForSMEC);
      if (targetCard) {
        const targetFormKey = checkTileFormStatus(targetCard);
        if (targetFormKey) {
          openWebForm(targetCard.name, targetCard.iriS_Utterance, targetCard.tileName, targetFormKey);
          sessionStorage.setItem("WebFormOpen", "true");
          setIsTileHasForm(true);
          setOpenPanelId(card.redirectServiceIdForSMEC);
          setIsRedirectFromAnotherForm(true);
          // Return true so callers (openPanel) don't override openPanelId back to the source tile
          return true;
        }
      }
    }

    const serviceWithFormKey = checkTileFormStatus(card);
    // Use openWebForm (not openWebFormFinal) to avoid re-triggering handleChatBotIconClick
    // which resets the copilot welcome message. Copilot is already initialized at this point.
    if (serviceWithFormKey) {
      openWebForm(card.name, card.iriS_Utterance, card.tileName, serviceWithFormKey);
      sessionStorage.setItem("WebFormOpen", "true");
      setIsTileHasForm(true);
    } else {
      setIsTileHasForm(false);
    }
    return false;
  };

  const dismissPanel = (isrecommended = false) => {
    setIsTileHasForm(false);
    setFormOpenRequestedForTileId(null);
    setOpenPanelId(null);
    setEnterprise(false);
    setIsRedirectFromAnotherForm(false);
    // Cancel any pending segment timer and clear state to prevent ghost rendering
    hideSegmentDropdown();
    setTileProfileResponse({ isSuccess: false, profileJson: null } as TileProfileResponse)
    // Clear P-URL flags so stale state doesn't block future DynamicDropdown auto-process
    sessionStorage.removeItem("PUrlSegmentPending");
    sessionStorage.removeItem("PUrlBotDeferred");
    sessionStorage.removeItem("WebFormOpen");
    // Start Event Logging //
    reduxDispatch(SETHasServiceRequestTypeLogData(true));

    reduxDispatch(
      SETLogEventData({
        elementName: isrecommended
          ? ai_servie_detail_panel_cancel
          : non_ai_service_detail_panel_cancel,
        elementId: "Cancel",
        action: "Click",
        message: isrecommended
          ? ai_service_detail_cancel_message
          : non_ai_service_detail_cancel_message,
        profileSetting: `v2`,
      })
    );

    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
  };

  const dismissPanelOnKeyPress = (e, id) => {
    if (e.key === "Enter") {
      setIsTileHasForm(false);
      setOpenPanelId(null);
      setEnterprise(false);
      setIsRedirectFromAnotherForm(false);
      // Cancel any pending segment timer and clear state to prevent ghost rendering
      hideSegmentDropdown();
      setTileProfileResponse({ isSuccess: false, profileJson: null } as TileProfileResponse)
      // Clear P-URL flags so stale state doesn't block future DynamicDropdown auto-process
      sessionStorage.removeItem("PUrlSegmentPending");
      sessionStorage.removeItem("PUrlBotDeferred");
      sessionStorage.removeItem("WebFormOpen");
      // Start Event Logging //
      reduxDispatch(SETHasServiceRequestTypeLogData(true));
      reduxDispatch(
        SETLogEventData({
          elementName: "Service Details Panel Cancel",
          elementId: "Cancel",
          action: "Key Press",
          message: "User Key Press on Service Details Panel Cancel",
          profileSetting: `v2`,
        })
      );

      reduxDispatch(SETIsCustomeLog(true));
      // End Event Logging //
    }
  };

  const GotoCatalogPage = async () => {
    // Start Event Logging //
    reduxDispatch(
      SETLogEventData({
        elementName: "Add more Help to My Help Workspace",
        elementId: "",
        action: "click",
        message: "User click on Add more Help to My Help Workspace",
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
    setActiveTabKey("1");  };

  const fetchWebFormNames = async () => {
    let webFormNames: any[] = localStorage.getItem(
      WEBFORMNAMES_LOCAL_STORAGE_KEY
    )
      ? JSON.parse(localStorage.getItem(WEBFORMNAMES_LOCAL_STORAGE_KEY)!)
      : [];

    if (webFormNames.length === 0) {
      // Fetch new data since it's not available in localStorage
      webFormNames = await getFormNames(context);
      // Store the result in localStorage with the versioned key
      localStorage.setItem(
        WEBFORMNAMES_LOCAL_STORAGE_KEY,
        JSON.stringify(webFormNames)
      );
    }
    setWebFormNames(webFormNames);
  };

  const cleanUpOldVersions = () => {
    const allKeys = Object.keys(localStorage);
    const currentKeyPrefix = "v2_webFormNames";

    allKeys.forEach((key) => {
      if (
        key.startsWith(currentKeyPrefix) &&
        key !== WEBFORMNAMES_LOCAL_STORAGE_KEY
      ) {
        localStorage.removeItem(key); // Remove old versioned data
      }
    });
  };

  function normalizeString(str: string | null | undefined): string {
    return (str || "")
      .normalize("NFKC") // Normalize Unicode characters
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width and BOM characters
      .replace(/\u00A0/g, " ") // Replace non-breaking spaces with regular spaces
      .replace(/\s+/g, " ") // Collapse multiple spaces into one
      .trim(); // Trim leading/trailing spaces
  }

  // Maps a catalog tile (V2_ServicesDTO) to workspace tile format (V2_UserWorkSpaceResult)
  // so the existing InfoPanel rendering and helpers can consume it seamlessly.
  const getFirstDropDownOptions = (serviceRequestFormLink) => {
    const arr = [];
    
    // Validate input
    if (!serviceRequestFormLink || typeof serviceRequestFormLink !== 'string') {
      return arr;
    }

    const trimmedLink = serviceRequestFormLink.trim();
    if (!trimmedLink.startsWith("[") || !trimmedLink.endsWith("]")) {
      return arr;
    }

    try {
      const parsedData = JSON.parse(trimmedLink);
      if (parsedData && Array.isArray(parsedData)) {
        elseConditionLoop(arr, parsedData);
      } else {
      }
    } catch (error) {
      console.error('Failed to parse serviceRequestFormLink:', error, 'Value:', serviceRequestFormLink);
    }
    
    return arr;
  };

  const openWebForm = async (
    name,
    iriS_Utterance,
    tileName,
    serviceWithFormKey
  ) => {
    // Derive durable Americas flag from the tile object so the BSO Americas
    // short_description gate no longer depends on the deprecating iriS_Utterance.
    const isAmerica =
      finalTileObject?.isAmerica === true ||
      finalTileObject?.isAmericaSMC === true;
    setWebFormIrisContext({
      name,
      iriS_Utterance,
      tileName,
      serviceWithFormKey,
      isAmerica,
    });
    setIsWebFormPanelOpen(true);
  };

  const closeWebFormPanel = () => {
    setIsWebFormPanelOpen(false);
  };

  const handleChatBotIconClick = async (
    iriS_Utterance,
    irisAppName,
    tileName,
    segment = "N/A",
    subsegment = "N/A"
  ) => {
    const isForm = sessionStorage.getItem("WebFormOpen");
    let sessionSubSegment = null;
    let sessionSegment = null;
    if (isForm === "true") {
      sessionSegment = sessionStorage.getItem("Segment");
      sessionSubSegment = sessionStorage.getItem("SubSegment");
    }
    setChatBotClicked(true);
    setIrisUtterance_shell(iriS_Utterance);
    setIrisAppName(irisAppName);
    setTileName_shell(tileName);
    setSegment(sessionSegment ? sessionSegment : segment);
    setSubSegment(sessionSubSegment ? sessionSubSegment : subsegment);
    reduxDispatch(setIsBotError(false));
    if (
      isForm === "true" &&
      sessionSegment !== null &&
      sessionSubSegment !== null
    ) {
      sessionStorage.setItem("WebFormOpen", "false");
      sessionStorage.removeItem("Segment");
      sessionStorage.removeItem("SubSegment");
    }
  };

  const deleteService = async (id) => {
    try {
      hideDeleteModal();
      setIsLoading(true);
      if (context ? context.telemetryClient : "") {
        //Removed useless assignment to variable "Upn"
        const responseServiceWorkspaceDelete = await deleteConsumptionAPI(
          `/api/V2_UserWorkSpace/Delete/${id}`,
          context.authClient
        );
        setIsLoading(false);
        setServiceDeleted(true);
        setDeleteId(responseServiceWorkspaceDelete.status);
        setServiceDeleted_shell(true);      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const showDeleteModal = (id) => {
    setDeleteModalId(id);
    setIsDeleteModalVisible(true);
  };

  const hideDeleteModal = () => {
    setDeleteModalId(null);
    setIsDeleteModalVisible(false);
  };

  const hideErrorModal = () => {
    setIsErrorModalVisible(false);
    setShowTileNotFoundModal({ isVisible: false, message: "", showCatalogButton: false });
  };

  const handleConfirmDelete = async (id) => {
    await deleteService(id);
    await getRecommendedTiles();

    // Find the service that was deleted
    const serviceDeleted = dataServiceWorkspace.find((tile) => tile.id === id);
    // Remove the visibility data from local storage if the service was found
    if (serviceDeleted !== undefined) {
      localStorage.removeItem(
        `TileVisibility_${userId}_${currentUserData.userArea}_${serviceDeleted.tileName}`
      );
    }
    hideDeleteModal();

    // Start Event Logging //

    let tile: any = selectedServiceTile as any;

    // Set the flag to indicate that service request type log data is available
    reduxDispatch(SETHasServiceRequestTypeLogData(true));

    // Set the log event data for the delete confirmation button click
    reduxDispatch(
      SETLogEventData({
        elementName: "Service Tile Delete Confirmation Yes",
        elementId: "Yes",
        action: "click",
        message: "User click on Service Tile Delete Confirmation Yes",
        profileSetting: `v2`,
      })
    );

    // Set the service request type log data
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: tile.name,
        RequestType: "",
        TileName: tile.tileName,
        IsNonIrisService: tile.service_IsNonIRISService ? "true" : "false",
        IrisUtterance: tile.iriS_Utterance,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
  };
  const handleCancelDelete = () => {
    hideDeleteModal();
  };

  const handleCancelError = () => {
    hideErrorModal();
  };

  const handleCancelDropdown = () => {
    setIsDropdownModalVisible(false);
    // Don't reset redirect state here - let natural flow handle it
  };

  const openModal = (newValue, openinBrowserTab = false) => {
    setValue(newValue);
    openIframe(newValue, openinBrowserTab);
  };

  const openInNewTab = (url) => {
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  };

  const openIframe = (iframeSrc, openinBrowserTab) => {
    try {
      if (!openinBrowserTab) {
        const iframe = document.createElement("iframe");
        iframe.src = iframeSrc;
        window.open(
          iframe.src,
          "thirdPartyPopup",
          "width=" +
            window.outerWidth * 0.9 +
            ", height=" +
            window.outerHeight * 0.7 +
            ", top=200, left=42, frameborder=0, allowfullscreen, toolbar=no ,location=0, status=no, titlebar=no, menubar=no"
        );
      } else {
        openInNewTab(iframeSrc);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openFAQs = (faqLink) => {
    window.open(faqLink, "_blank");
  };

  const GetPanelWidth = (isLarge, htmlContent) => {
    let panelWidth = "25vw";

    let tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Access the table and count the number of columns in the first row
    let table = tempDiv.querySelector(".Table");
    if (table) {
      let firstRow = table.querySelector("tr");
      let numberOfColumns = firstRow.children.length;

      if (numberOfColumns > 1) {
        panelWidth = "50vw";
      }

      AdjustColumnWidth(numberOfColumns);
    }
    return panelWidth;
  };

  const renderTile = (data, isRecommended) => {
  
    return (
    <div className={styles.section}>
      <div className={styles.container}>
        {data.length > 0 ? (
          data.map((card, index) => (
            <React.Fragment key={card.serviceID || index}>
              <CardElements
                title={resolvePdmSupportAssuranceTitle(card.name)}
                aboutService={card.aboutService}
                displayName={card.displayName}
                onDelete={() => {
                  setSelectedServiceTile(card);
                  showDeleteModal(card.id);
                }}
                isSegment={card.isSegment}
                serviceID={card.serviceID}
                isRecommended={isRecommended}
                faqLink={card.faqLink}
                help_Provider={card.help_Provider}
                slA_logo={card.slA_logo}
                slA_in_days={card.slA_in_days}
                setIrisBotIconClicked={setIrisBotIconClicked}
                checkIrisService={() => {                  
                  return checkIrisService(
                    resolvePdmSupportAssuranceTitle(card.name),
                    card.service_IsNonIRISService,
                    card.iriS_Utterance,
                    card.serviceRequestFormLink,
                    card.irisAppName,
                    card.tileName,
                    card.serviceID,
                    card.openinBrowserTab,
                    isRecommended,
                    card.isSegment,
                    card.appName
                  );
                }}
                styles={styles}
                tileSelected={tileSelected}
                validateURL={validateURL}
                closeMCAPSPanel={closeMCAPSPanel}
                setDetailClick={setDetailClick}
                handleStartClick={handleStartClick}
                setTileSelected={setTileSelected}
                handleDetailClick={handleDetailClick}
                setStartClick={setStartClick}
                addToWorkspace={addToWorkspace}
                openFAQs={openFAQs}
                openPanel={openPanel}
              />

              {openPanelId === card.serviceID && (
                <div className="infopanle-overlay" />
              )}

              {/* Move InfoPanel inside the wrapping div */}
              {openPanelId === card.serviceID && (
                <Suspense fallback={<></>}>
                  <InfoPanel
                    isOpen={openPanelId === card.serviceID}
                    dismissPanel={dismissPanel}
                    card={{
                      ...card,
                      name: resolvePdmSupportAssuranceTitle(card.name),
                    }}
                    buttonText="Start"
                    buttonSymbol=""
                    checkIrisService={checkIrisService}
                    customWidth={GetPanelWidth(
                      card.service_IsLarge,
                      card.serviceCategories
                    )}
                    dismissPanelOnKeyPress={dismissPanelOnKeyPress}
                    userId={userId}
                    isrecommended={isRecommended}
                    isDetailClick={isDetailClick}
                    setDetailClick={setDetailClick}
                    isPanelOpen={isWebFormPanelOpen}
                    closeWebFormPanel={closeWebFormPanel}
                    formDataToIris={setFormDataToIris}
                    showBot={setShowBot}
                    irisContext={webFormIrisContext}
                    chatBotIconClick={handleChatBotIconClick}
                    isTileHasForm={isTileHasForm}
                    validateURL={validateURL}
                    isEneterprise={isEneterprise}
                    tileProfileResponse={tileProfileResponse}
                    setTileProfileResponse={setTileProfileResponse}
                    setAllowRendering={setAllowRendering}
                    isRedirectFromAnotherForm={isRedirectFromAnotherForm}
                    firstDropDownOptions={getFirstDropDownOptions(card.serviceRequestFormLink)}
                    handoffConfigured={(() => {
                      const entry = copilotAccessMap[resolveTileHandoffKey(copilotAccessMap, card.tileName || '', card.name)];
                      return entry?.handoffConfigured === true || entry?.handoff != null;
                    })()}
                    enableTileForm={(() => {
                      const tileKey = (card.tileName || '').toLowerCase();
                      let mapEntry = copilotAccessMap[tileKey];
                      // Also check redirect target (e.g. AMR Americas → AMR)
                      if (!mapEntry?.hasAccess && card.isRedirect && card.redirectServiceIdForSMEC) {
                        const targetCard = dataServiceWorkspace.find((srv) => srv.serviceID === card.redirectServiceIdForSMEC);
                        mapEntry = copilotAccessMap[(targetCard?.tileName || '').toLowerCase()];
                      }
                      // Allow form-open override from copilot-triggered actions (e.g. "Create Incident" button)
                      if (formOpenRequestedForTileId === card.serviceID) return true;
                      // CopilotADGroupMapping.enableTileForm = 0 → webform must NOT open; Service Info should show instead
                      if (mapEntry?.enableTileForm === false) return false;
                      const isCopilotOnly = mapEntry?.hasAccess === true && mapEntry?.enableSelfHelp === true;
                      return !isCopilotOnly;
                    })()}
                    fetchTileProfile={fetchTileProfile}
                    handleStartButtonFormLogic={handleStartButtonFormLogic}
                    authClient={context?.authClient?.authContext}
                    userUpn={(context?.authClient?.authContext as any)?.getActiveAccount?.()?.username || ""}
                    telemetryClient={context?.telemetryClient}
                    formOpenRequested={formOpenRequestedForTileId === card.serviceID}
                    setFormOpenRequested={(val: boolean) => setFormOpenRequestedForTileId(val ? card.serviceID : null)}
                  />
                </Suspense>
              )}

              {deleteModalId === card.id && (
                <Suspense fallback={<></>}>
                  <PopUpModal
                    isVisible={isDeleteModalVisible}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    onHide={hideDeleteModal}
                    itemId={deleteModalId}
                    dialogTitle="Confirm Removal"
                    dialogSubText="Are you sure you want to remove this service? You can always find it again in the Catalog."
                    buttonsCount={2}
                    buttonTextOne="Yes"
                    buttonTextTwo="No"
                  />
                </Suspense>
              )}

              {/* P-URL Service Not Found Modal */}
              {showTileNotFoundModal.isVisible && showTileNotFoundModal.showCatalogButton && (
                <Suspense fallback={<></>}>
                  <PopUpModal
                    isVisible={showTileNotFoundModal.isVisible}
                    onConfirm={() => {
                      window.history.replaceState({}, '', window.location.pathname);
                      setActiveTabKey('0');
                      setShowTileNotFoundModal({ isVisible: false, message: "", showCatalogButton: false });
                    }}
                    onCancel={() => setShowTileNotFoundModal({ isVisible: false, message: "", showCatalogButton: false })}
                    onHide={() => setShowTileNotFoundModal({ isVisible: false, message: "", showCatalogButton: false })}
                    itemId={null}
                    dialogTitle="Service Not Found"
                    dialogSubText={showTileNotFoundModal.message}
                    buttonsCount={1}
                    buttonTextOne="Browse Catalog"
                  />
                </Suspense>
              )}

              <SegmentSelector
                selectedTileId={selectedTileId}
                card={card}
                selectedOptions={selectedOptions}
                dialogTitle={dialogTitle}
                isDropdownModalVisible={isDropdownModalVisible}
                value={value}
                openModal={openModal}
                handleCancelDropdown={handleCancelDropdown}
                segmentOptions={segmentOptions}
                segmentDropdownVisible={segmentDropdownVisible}
                hideSegmentDropdown={hideSegmentDropdown}
                hideSegmentDropdownAfterSave={hideSegmentDropdownAfterSave}
                subSegmentOptions={subSegmentOptions}
                passSegmentDataToIris={passSegmentDataToIris}
                getAllSubSegmentFromSegment={getAllSubSegmentFromSegment}
                setEnterprise={setEnterprise}
                isTileHasForm={isTileHasForm}
                dismissPanel={dismissPanel}
                setOpenPanelId={setOpenPanelId}
                openPanelForSMEC={openPanelForSMEC}
                dataServiceWorkspace={dataServiceWorkspace}
                isopenSegment={isopenSegment}
                userSelectedOption={userSelectedOption}
                isRedirectFromAnotherForm={isRedirectFromAnotherForm}
                tileProfileResponse={tileProfileResponse}
                allowRendering={allowRendering}
                isDetailClick={isDetailClick}
                openWindowFrame={openWindowFrame}
              />
            </React.Fragment>
          ))
        ) : (
          <></>
          // <p>No help available</p>
        )}
        <button
          type="button"
          className={styles.findMoreCard}
          onClick={GotoCatalogPage}
        >
          <Text
            style={{
              color: "#333",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "600",
              lineHeight: "24px",
            }}
          >
            Need more help?
          </Text>
          <Text
            style={{
              color: "#323130",
              textAlign: "center",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
            }}
          >
            Try searching our catalog.
          </Text>
        </button>
      </div>
    </div>
  );
  };

  const renderTiles = (isRecommended = false) =>
    !isRecommended
      ? renderTile(dataServiceWorkspace, isRecommended)
      : renderTile(recommendedTile, isRecommended);

  const renderError = () => (
    <div
      style={{
        justifyContent: "center",
        alignContent: "center",
        marginRight: 30,
        width: "50%",
      }}
    >
      <ErrorComponent errorData={errorData} />
    </div>
  );

  const loadExistingTiles = () => {
    let content;
    if (resultError === "success") {
      if (dataServiceWorkspace.length > 0) {
        content = renderTiles();
      } else {
        content = <></>; // or some other default content
      }
    } else {
      content = renderError();
    }

    return content;
  };

  const loadRecommendationTile = () => {
    let data;
    if (ResultErrorRecommendation === "success") {
      if (recommendedTile.length > 0) {
        data = renderTiles(true);
      } else {
        data = (
          <>
            <Label
              style={{
                backgroundColor: "white",
                padding: "1vh",
                marginTop: "2vh",
              }}
            >
              {AI_EMPTY_LABEL}
            </Label>
          </>
        );
      }
    } else if (ResultErrorRecommendation === "error") {
      data = renderError();
    } else {
      <></>;
    }
    return data;
  };

  const AdjustColumnWidth = (columnCount) => {
    setTimeout(() => {
      let tableItems = document.querySelectorAll(".panelContainer table td");

      let columnClass = "onecolumn";
      if (columnCount > 1) {
        columnClass = "twocolumn";
      }
      tableItems.forEach((tableItems) => {
        if (tableItems) tableItems.classList.add(columnClass);
      });
    }, 100);
  };

  // Service routing hook — extracted 10 interlinked routing functions (Phase 2)
  const {
    checkIrisService,
    openPanel,
    openPanelOnKeyPress,
    openPanelForCatalogTile,
    openWindowFrame,
    checksIris,
    elseConditionLoop,
  } = useServiceRouter({
    dataServiceWorkspace,
    webFormNames,
    selectedTileId,
    setAllowRendering,
    setOpenPanelId,
    setSelectedTileId,
    setIsTileHasForm,
    setIsErrorModalVisible,
    setSegmentDropdownVisible,
    setDataServiceWorkspace,
    setDialogTitle,
    setSelectedOptions,
    setIsDropdownModalVisible,
    handlePanelOpening,
    fetchTileProfile,
    handleStartButtonFormLogic,
    handleChatBotIconClick,
    openWebFormFinal,
    openWebForm,
    handleFormAndSegment,
    openIframe,
    normalizeString,
    reduxDispatch,
    copilotAccessMap,
    copilotAccessMapLoaded: copilotAccessMapLoaded.current,
    loadCopilotAccessMap: async () => {
      const request = ++copilotAccessRequest.current;
      const map = await loadCopilotAccessMap(context.authClient, sessionStorage.getItem("LoggedInUser") || '');
      if (request === copilotAccessRequest.current) setCopilotAccessMap(map);
      return map;
    },
    onHandoffStart: (tileId, config) => trackTileAgentHandoff(context.telemetryClient, 'Start', tileId, config),
    props: { setShowBot, enableWwicCopilot },
  });

  // Ref-bridge: populate forward-referenced callbacks for hooks
  callbacksRef.current = {
    checkTileSecurity, checkIrisService, checksIris, handleChatBotIconClick,
    openWebForm, openPanel, PresetPreference, checkTileFormStatus,
    handlePanelOpening, setInfoPanelZIndex, handleStartClick,
  };

  return (
    <>
      {isLoading && <ShimmerLoader count={2} />}
      {!isLoading && (
        <div className="tilecontainer">
          <label className="servicesLabel" htmlFor="myHelp">
            <h3
              style={{
                color: "black",
                fontFamily: "Segoe UI",
                fontSize: "18px",
                fontWeight: "600",
                lineHeight: "24px",
                marginBottom: "8px",
              }}
            >
              My Help
            </h3>
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", marginLeft: "2px" }}>
            {loadExistingTiles()}
            {dataServiceWorkspace.length == 0 && (
              <div className={styles.findMoreCard} onClick={GotoCatalogPage}>
                Find more Help
              </div>
            )}
          </div>
          <div></div>
        </div>
      )}
      {currentUserData?.enableAIRecommendation &&
        isLoadingRecommended &&
        currentUserData?.isAiUser &&
        currentUserData?.isAiServicesEnabled && (
          <ShimmerLoader count={2} />
        )}
      {currentUserData?.enableAIRecommendation &&
        !isLoadingRecommended &&
        currentUserData?.isAiUser &&
        currentUserData?.isAiServicesEnabled &&
        recommendedTile.length > 0 && (
          <>
            <div>
              <div className="tilecontainer">
                <label className="servicesLabel" htmlFor="myHelp">
                  <h3
                    style={{
                      color: "black",
                      fontFamily: "Segoe UI",
                      fontSize: "18px",
                      fontWeight: "600",
                      lineHeight: "24px",
                      marginBottom: "8px",
                    }}
                  >
                    AI Recommended Services
                  </h3>

                  <text className="aiText">
                    AI-generated Services may be inaccurate
                  </text>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    marginLeft: "2px",
                  }}
                >
                  {loadRecommendationTile()}
                </div>
              </div>
            </div>
          </>
        )}
    </>
  );
};
export const V2_Tile = injectIntl(TileComponent);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: "20px",
    padding: "20px",
  },
  headerconatiner: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    flex: 1, // Allow this section to grow based on content
    overflow: "visible", // Allow overflow to be visible and prevent clipping
    paddingBottom: "10px", // Add padding at the bottom to allow extra space for description
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    margin: "0px auto",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    marginLeft: "25px",
  },
  //   border-radius: 8px;
  // border: 2px dashed var(--default-accent-communication-blue-theme-primary, #0078D4);
  // background: rgba(0, 120, 212, 0.06);
  findMoreCard: {
    flexDirection: "column",
    width: "316px",
    height: "360px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed  #0078D4",
    borderRadius: "12px",
    cursor: "pointer",
    textAlign: "center",
    background: "rgba(0, 120, 212, 0.06)",
  },
  cardcomp: {
    position: "relative",
    borderTopWidth: "4px",
    width: "316px",
    maxWidth: "100%",
    padding: "26px",
    borderRadius: "7px",
    backgroundColor: "#ffffff",
    borderBlockWidth: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    height: "auto",
    minHeight: "360px", // Allow card height to adjust based on content
    maxHeight: "400px", // Set max height to avoid cards becoming too large
    overflow: "hidden", // Hide overflow content if necessary
    "&::before, &::after": {
      content: "none !important",
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    transition:
      "transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.48)", // Maintain box shadow
      backgroundColor: "white", // Keep the background color on hover
      transform: "translateY(-10px)", // Shift the card up slightly (no scale)
    },
  },

  title: {
    fontSize: "18px",
    fontWeight: "bold",
    overflowWrap: "break-word", // Allow long words to break and wrap to the next line
    wordBreak: "break-word", // Break words if needed, to avoid overflow
    whiteSpace: "normal", // Allow title text to wrap normally
    lineHeight: "1.4", // Provide some spacing between lines of text
  },
  description: {
    fontSize: "14px",
    color: tokens.colorNeutralForeground2,
    display: "-webkit-box", // Use a flex container with multiple lines
    WebkitBoxOrient: "vertical", // Set to vertical for multi-line text
    overflow: "hidden", // Hide overflow content
    WebkitLineClamp: 4, // Limit the text to 2 lines
    whiteSpace: "normal", // Allow wrapping of text
    textOverflow: "ellipsis", // Add ellipsis (...) if the content overflows
    cursor: "default", // Change cursor on hover to indicate interactivity
    marginTop: "5px", // Optional: Add some space between the title and description
    // "&:hover": {
    //   WebkitLineClamp: "unset", // Remove line clamping on hover to show the full description
    //   whiteSpace: "normal", // Allow text to wrap completely on hover
    //   textOverflow: "unset", // Remove ellipsis on hover
    // },
  },

  infoSection: {
    fontSize: "12px",
    display: "flex",
    flexDirection: "column", // Stack content vertically
    alignItems: "flex-start",
    gap: "5px",
    flex: 1,
    marginTop: "10px",
  },
  rightColumn: {
    display: "flex",
    justifyContent: "flex-end",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "row", // Ensures vertical stacking
    alignItems: "center", // Prevent overflow
  },
  iconImage: {
    width: "auto",
    height: "24px",
  },
  slaContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "10px",
    alignItems: "center",
    borderTop: "1px solid #D1D1D1",
    paddingTop: "10px",
    flex: 1,
    maxHeight: "60px",
    marginTop: "10px",
  },

  slaRow: {},

  slaTitle: {
    color: "#595959",
    fontSize: "12px",
    textAlign: "left",
  },

  ownerTitle: {
    color: "#595959",
    fontSize: "12px",
    textAlign: "left",
  },

  slaValue: {
    fontSize: "14px",
    textAlign: "left",
  },
  ownerValue: {
    fontSize: "14px",
    textAlign: "left",
  },

  hoverContainer: {
    position: "relative",
    width: "100%",
    paddingBottom: "10px",
  },

  infosectionconatiner: {
    width: "100%",
  },

  actionhoverMenu: {
    bottom: "4px",
    width: "308px",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)", // Center the menu horizontally
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    background: "#0078D4",
    padding: "24px 0",
    borderRadius: "6px",
    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
    opacity: 0, // Initially hidden
    visibility: "hidden", // Initially hidden
    transition: "opacity 0.3s ease, visibility 0.3s ease", // Smooth fade-in/fade-out
  },

  actionhoverMenuVisible: {
    opacity: 1, // Show the menu when hovered
    visibility: "visible", // Make it visible
  },

  actionButton: {
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: "265px",
    color: "white",
    backgroundColor: "transparent",
    border: "none",
    padding: "6px 12px",
    margin: "0",
    borderRadius: "5px",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: "#005A9E",
    },
  },

  cardHover: {
    position: "relative",
    transform: "scale(1.05)",
    transition: "transform 0.3s ease-in-out",
  },

  readMoreButton: {
    marginTop: "8px",
    color: "blue",
    cursor: "pointer",
    background: "none",
    border: "none",
    textDecoration: "underline",
  },
  deleteIcon: {
    position: "absolute",
    bottom: "1px",
    right: "10px",
    cursor: "pointer",
    fontSize: "18px",
  },
  catalogTileFocusable: {
    "&:focus": {
      outline: "none",
    },
  },
    "&:focus": {
      outline: "none"
    },
  },
);

// Generated by Copilot
// updated on: 2025-09-17 | 3 lines updated | [Manual] | Added explicit PropsWithChildren typing to ensure proper typing for children after upgrading from React 18.1 to 18.3.1 | Time Taken 20 min
// updated on: 2025-11-10 | 3 lines updated | [Manual] | Updated the padding for the Tile | Time Taken 2 min
// updated on: 2025-11-02 | 310 lines added/updated | [Auto Generated] | Implemented tile profile processing, segment handling, dynamic dropdowns, InfoPanel integration, form handling | Time Taken 45 min
// updated on: 2025-11-07 | 12 lines added/updated | [Auto Generated] | Handled dropdown visibility based on segment availability | Time Taken 5 min
// updated on: 2026-02-26 | 180 lines added | [Auto Generated] | P-URL catalog fallback: when tile not pinned in workspace, fetch from catalog (v2_services), map to workspace format, open flyout + IRIS without pinning. Handles incident, SR, ECIF, segment, copilot tile types. Respects DefaultToRelatedInfo and isSecuredByAzureADGroup | Time Taken 30 min
// updated on: 2026-02-26 | 2 lines updated | [Auto Generated] | Bug fix: removed isPUrlAccess restriction — tileName-only P-URLs (e.g. ECIF AC4/AC5) now trigger catalog fallback. Previously required Mode/IrisUtterance/TileInfo params which ECIF URLs don't have | Time Taken 5 min
// updated on: 2026-02-26 | 1 line updated | [Auto Generated] | Bug fix: hasTileName check now case-insensitive using Array.from(keys).some(toLowerCase) to match wiki URLs using Tilename variant | Time Taken 2 min
// updated on: 2026-02-26 | 25 lines updated | [Auto Generated] | ECIF rename resilience (WI-1213250): workspace + catalog search now exact-match first, then ECIF env-var fallback for old M365 name, then unambiguous partial match only. Prevents "ECIF Support Services" from partial-matching "ECIF Support Services M365" | Time Taken 10 min
// updated on: 2026-03-03 | 25 lines added/updated | [Auto Generated] | Fixed PURL first-load failure: changed fallback API from GetAllRecommendedWorkspaces to GetAllServices (full catalog), added SG-lock detection with PURL_ERROR_TILE_LOCKED error, populated v2_services sessionStorage from API response to solve race condition | Time Taken 15 min
// updated on: 2026-03-03 | 10 lines added | [Auto Generated] | Fixed PURL irisUtterance URL param being ignored - URL param value now overrides tile DB default for Iris pre-fill while preserving Americas/non-Americas routing logic | Time Taken 5 min
// updated on: 2026-03-03 | 5 lines updated | [Auto Generated] | Fixed React 17 race condition: moved irisUtterance URL override into unstable_batchedUpdates to prevent AppFooter reading empty value from web form path before override fires | Time Taken 10 min
// updated on: 2026-03-19 | 9 lines added/updated | [Auto Generated] | Zero-deploy tile routing: replaced === with startsWith() + split('|') to parse copilot appName from IrisAppName pipe convention (MCAPSHelpCopilot|SOS). Conditional spread prevents empty appName in event dispatch | Time Taken 15 min
// updated on: 2026-03-19 | 12 lines updated | [Auto Generated] | Refactored to use dedicated DB AppName column instead of pipe convention. Reverted startsWith to === equality, replaced split('|') parsing with direct tile.appName from API response. Added appName parameter to checkIrisService, ifandelsecondition, and IsNonIrisService call chains | Time Taken 10 min
// updated on: 2026-04-01 | 30 lines added | [Auto Generated] | T13: Per-user copilot gating — calls GetCopilotType API before routing to MCAPSHelp Copilot, falls back to IRIS if user not in AD group (Story #1229620) | Time Taken 10 min
// updated on: 2026-07-07 | 12 lines added/updated | [Auto Generated] | FYRO: Enterprise/MS Elevate parity - in handleStartButtonFormLogic, MS Elevate fell into the SME&C/Corp redirect-to-form block (only "enterprise" was matched), so MS Elevate opened the redirect TARGET form while Enterprise stayed on the source form/IFrame. Now both "enterprise" and "ms elevate" set isEnterprise=true, so both stay on the same source form (e.g. "Account Management Requests (Americas Enterprise only)") / IFrame (AM Tool); only SME&C/Corp redirect | Time Taken 6 min
// updated on: 2026-08-18 | 3 lines added | [Auto Generated] | Applied the feature-selected PDM Support Assurance title to V2 workspace cards | Time Taken 5 min

// Generated by Copilot
// updated on: 2026-09-15 | 8 lines added | [Auto Generated] | Story #1296698: openWebForm now derives durable isAmerica from finalTileObject and passes it into webFormIrisContext, so the BSO Americas short_description gate no longer relies on the deprecating iriS_Utterance (live V2 path) | Time Taken 2 min
// Generated by Copilot | 2026-10-26 | 8 lines added | Auto | 1321277 load authenticated handoff configuration before legacy Start routing | Time Taken 4 min
// Generated by Copilot | 2026-10-26 | 3 lines added | Auto | 1321278 connect genuine Start actions to platform telemetry | Time Taken 2 min
// Generated by Copilot | 2026-10-26 | 10 lines added/updated/removed | Auto | 1322441 refresh shared access and handoff map without standalone API calls | Time Taken 4 min
// Generated by Copilot | 2026-10-27 | 8 lines added/updated/removed | Auto | 1322441 distinguish a successful access map from pending or failed prefetch | Time Taken 4 min
// Generated by Copilot | 2026-10-27 | 5 lines added | Auto | 1322441 pass configured handoff candidates into Service Info Start routing | Time Taken 2 min
// Generated by Copilot | 2026-10-27 | 5 lines added/updated/removed | Auto | 1322441 use shared handoff-key resolution for Service Info candidate detection | Time Taken 2 min